/**
 * Converts RGB values to a hex color value.
 * @param r The red component (0-255).
 * @param g The green component (0-255).
 * @param b The blue component (0-255).
 * @returns A hex color string (e.g., '#ff5733') or throws an error for invalid input.
 * @throws Will throw an error if RGB values are out of range.
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  // Validate RGB input
  const isValidColorValue = (value: number) =>
    Number.isInteger(value) && value >= 0 && value <= 255;

  if (![r, g, b].every(isValidColorValue)) {
    throw new Error('RGB values must be integers between 0 and 255');
  }

  const hexR = r.toString(16).padStart(2, '0');
  const hexG = g.toString(16).padStart(2, '0');
  const hexB = b.toString(16).padStart(2, '0');

  return `#${hexR}${hexG}${hexB}`;
};

/**
 * Converts a hex value to RGB values.
 * @param hex A regular or shortened hex value (e.g., #123 or #123456).
 * @returns An object containing the RGB values, or null if the input is invalid.
 * @throws Will throw an error if the hex color format is invalid.
 */
export const hexToRgb = (hex: string) => {
  const hexToRgbRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

  const fullHex = shortHexToFullHex(hex);
  const values = hexToRgbRegex.exec(fullHex);

  if (!values) {
    throw new Error('Invalid hex color format');
  }

  const r = Number.parseInt(values[1], 16);
  const g = Number.parseInt(values[2], 16);
  const b = Number.parseInt(values[3], 16);

  return `${r}, ${g}, ${b}`;
};

/**
 * Converts a shortened hex value (3 characters) to a full hex value (6 characters).
 * @param hex A regular or shortened hex value (e.g., #123 or #123456).
 * @returns The full (6 character) hex value or the original hex if it's valid.
 */
export const shortHexToFullHex = (hex: string) => {
  const hexShorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const cleanedHex = hex.replace(/^#/, '');

  if (hexShorthandRegex.test(cleanedHex)) {
    return `#${cleanedHex.replace(hexShorthandRegex, (_, r, g, b) => r + r + g + g + b + b)}`;
  }

  return `#${cleanedHex}`; // Return original hex if not shorthand
};
